





















































































































































































import moment from 'moment';
import {
  computed,
  defineComponent,
  onMounted,
  reactive,
  ref
} from '@vue/composition-api';
import { createNamespacedHelpers, useStore } from 'vuex-composition-helpers';

import { IRootState } from '@/store';
import {
  ITariffActions,
  ITariffGetters,
  ITariffMutations,
  ITariffState
} from '@/store/tariff';
import { ISystemError, ITariff } from '@/models';

import { setErrorFromAxiosError } from '@/utils/helpers';
import { IFetchTariffParams } from '@/services';
import authMixin from '@/mixins/auth';

import ALPage from '@/components/core/Page/ALPage.vue';
import ConfirmationDialog from '@/components/common/ConfirmationDialog.vue';
import ErrorAlert from '@/components/common/ErrorAlert.vue';
import TariffFormDialog from '@/components/tariff/TariffFormDialog.vue';
import TariffSearchFilter from '@/components/tariff/TariffSearchFilter.vue';

export default defineComponent({
  name: 'TariffListPage',

  mixins: [authMixin],

  components: {
    ALPage,
    ConfirmationDialog,
    ErrorAlert,
    TariffFormDialog,
    TariffSearchFilter
  },

  setup(_, { root }) {
    const filterParams = reactive<IFetchTariffParams>({
      destination_country: root.$route.query.destination_country as string,
      tariff_type: root.$route.query.tariff_type
        ? Number(root.$route.query.tariff_type as string)
        : undefined
    });

    const isSearchFilterActive = ref(false);
    const extraFiltersApplied = computed<boolean>(() =>
      Object.keys(filterParams).some(
        key => filterParams[key as keyof IFetchTariffParams] != undefined
      )
    );

    const currentSearch = ref<string>('');

    const headers = [
      {
        text: 'Destination Country',
        value: 'destination_country',
        sortable: false
      },

      { text: 'Tariff Type', value: 'tariff_type', sortable: false },

      {
        text: 'Target',
        value: 'target',
        sortable: false
      },

      {
        text: 'Tariff Rate (%)',
        value: 'tariff_rate',
        width: 200,
        sortable: false
      },

      { text: '', value: 'actions', sortable: false, width: 100 }
    ];

    const dialog = ref(false);
    const selectedTariff = ref<ITariff | null>(null);

    const store = useStore<IRootState>();

    const { useActions, useGetters } = createNamespacedHelpers<
      ITariffState,
      ITariffGetters,
      ITariffActions,
      ITariffMutations
    >(store, 'tariff');

    const { fetchTariffRates, deleteTariffRate } = useActions([
      'fetchTariffRates',
      'deleteTariffRate'
    ]);

    function openTariffDialog(tariff?: ITariff) {
      dialog.value = true;
      selectedTariff.value = tariff ?? null;
    }

    const deleteDialog = ref(false);
    const deleteError = ref<ISystemError | null>(null);
    const isDeleteInProgress = ref(false);

    function closeDeleteDialog() {
      deleteDialog.value = false;
    }

    function openDeleteTariffDialog(tariff: ITariff) {
      selectedTariff.value = tariff;
      deleteDialog.value = true;
    }

    function removeTariff(tariff: ITariff) {
      isDeleteInProgress.value = false;
      deleteTariffRate(tariff.id!)
        .then(() => closeDeleteDialog())
        .catch(err => {
          deleteError.value = setErrorFromAxiosError(err);
        })
        .finally(() => {
          isDeleteInProgress.value = false;
        });
    }

    const error = ref<ISystemError | null>(null);
    const isLoading = ref(false);

    function clearAllErrors() {
      deleteError.value = null;
      error.value = null;
    }

    const { getTariffRates, getTariffTypeVisualByType } = useGetters([
      'getTariffRates',
      'getTariffTypeVisualByType'
    ]);

    function loadTariffs(params: IFetchTariffParams) {
      clearAllErrors();

      /* this will update the URL query based on param values. */
      root.$router
        .replace({
          name: root.$route.name!,
          query: params as Record<string, any>
        })
        .catch((e: Error) => e); //catch navigation duplication error

      isLoading.value = true;

      return fetchTariffRates(params)
        .catch(err => {
          error.value = setErrorFromAxiosError(err);
        })
        .finally(() => {
          isLoading.value = false;
        })
        .then(() => {
          filterParams.destination_country = params.destination_country;
          filterParams.tariff_type = params.tariff_type;
        });
    }

    onMounted(() => loadTariffs(filterParams));

    return {
      isDeleteInProgress,
      deleteDialog,
      deleteError,
      selectedTariff,
      moment,
      dialog,
      currentSearch,
      tariffs: getTariffRates,
      isLoading,
      headers,
      error,
      openTariffDialog,
      clearAllErrors,
      openDeleteTariffDialog,
      closeDeleteDialog,
      removeTariff,
      loadTariffs,
      getTariffTypeVisualByType,
      isSearchFilterActive,
      extraFiltersApplied,
      filterParams
    };
  }
});
